import { useMemo, useState } from 'react';
import { IPaginatedData, QueryManyBuilder } from '@app/common';
import { useReactTable, getCoreRowModel, PaginationState, ColumnDef } from '@tanstack/react-table';
import { TypedUseQueryHookResult } from '@reduxjs/toolkit/dist/query/react';
import { baseQueryWithReauth } from '@app/store/api';

interface Props<TData> {
  columns: ColumnDef<TData, any>[];
  useQuery: (queryParams: Pick<QueryManyBuilder<TData>, 'pagination'> & { search: string }) => TypedUseQueryHookResult<IPaginatedData<TData>, QueryManyBuilder<TData>, typeof baseQueryWithReauth>
}

export const useTable = <TData,>({ columns, useQuery }: Props<TData>) => {
  const [search, setSearch] = useState('');

  const [{ pageIndex, pageSize }, setPagination] = useState<PaginationState>({
    pageIndex: 0,
    pageSize: 32,
  });

  const pagination = useMemo<PaginationState>(
    () => ({
      pageIndex,
      pageSize,
    }),
    [pageIndex, pageSize]
  )

  const { data, isFetching } = useQuery({
    search,
    pagination: {
      skip: pagination.pageIndex * pagination.pageSize,
      take: pagination.pageSize
    }
  });

  // Gestion générique de la forme de la réponse :
  // - cas paginé standard : { data: TData[], metadata: { total } }
  // - cas simple tableau : TData[]
  const payload: any = data;

  const rows: TData[] = Array.isArray(payload)
    ? payload
    : (payload?.data ?? []);

  const total: number = Array.isArray(payload)
    ? payload.length
    : (payload?.metadata?.total ?? rows.length ?? 0);

  const table = useReactTable({
    data: rows,
    columns,
    getCoreRowModel: getCoreRowModel(),
    pageCount: Math.ceil((total || 1) / pageSize),
    state: {
      pagination,
    },
    onPaginationChange: setPagination,
    manualPagination: true,
  });

  return {
    isFetching,
    table,
    data,
    setSearch
  }
}